import { ArrowLeft20, Close20, Search16 } from '@carbon/icons-react';
import { isNaN } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { Space, SpaceTab } from 'api/space';
import { RoutePaths } from 'constant';
import { useIsTablet } from 'hooks/use-media-query';
import { useTranslate } from 'hooks/use-translation';
import { SearchInput } from '../../components/cp-input';
import { useGetBackground } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { useCustomerPortalHashMap } from '../../utils';
import { BackIconWrapper, BackWrapper } from '../chat/components';
import { CpSectionWrapper } from '../common';
import { CpWidgetHeader } from '../common/header';
import { CpSpacesForLaptop } from './cp-spaces.laptop';
import { CpSpaceForMobile } from './cp-spaces.mobile';
import {
  ListOrGridMode,
  SPOTLIGHT_FOLDER_ID,
  RECENTS_FOLDER_ID,
  UrlType,
  CpSpacesData,
} from './types';

export const StyledBackWrapperInCpSpaces = styled(BackWrapper)`
  padding: 0;
  margin-bottom: var(--cp-spacing-07);
  display: flex;
  align-items: center;
  ${BackIconWrapper} {
    height: 42px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: start;
    padding: 0;
    margin-right: 0;
    @media (min-width: 481px) {
      margin-top: var(--cp-spacing-03);
    }

    @media (max-width: 480px) {
      margin-top: var(--cp-spacing-02);
    }
  }
`;

export const CpSpaces: React.FC<CpWidgetProps> = ({
  widget,
  data: _data,
  projectId,
  disabled,
}) => {
  const router = useHistory();
  const isResponsive = useIsTablet();
  const background = useGetBackground(widget);
  const data = useMemo(() => _data as CpSpacesData, [_data]);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<ListOrGridMode>(ListOrGridMode.Grid);
  const { hashMap, setHashMap, removeKey } = useCustomerPortalHashMap();
  const translate = useTranslate();
  const _currentSpace: Space | undefined = useMemo(() => {
    const isCurrentSpaceANumber = !isNaN(Number(hashMap?.space));

    if (isCurrentSpaceANumber) {
      const spaceId = Number(hashMap?.space);

      return (data?.spaces ?? []).find((sp) => sp.spaceId === spaceId);
    }
    switch (hashMap?.space) {
      case 'spotlight':
        return {
          private: false,
          project: {
            projectId,
          },
          spaceId: SPOTLIGHT_FOLDER_ID,
          spaceName: translate('spotlight'),
          spaceTabs: data?.spotlightedSpaceTabs,
        } as Space;
      case 'recents':
        return {
          private: false,
          project: {
            projectId,
          },
          spaceId: RECENTS_FOLDER_ID,
          spaceName: translate('recents'),
          spaceTabs: data?.recents,
        } as Space;

      default:
        if (!isResponsive) {
          const firstSpace = data?.spaces?.at(0);

          if (firstSpace) {
            return firstSpace;
          }

          return {
            private: false,
            project: {
              projectId,
            },
            spaceId: RECENTS_FOLDER_ID,
            spaceName: translate('recents'),
            spaceTabs: data?.spotlightedSpaceTabs,
          } as Space;
        }

        return undefined;
    }
  }, [
    data?.recents,
    data?.spaces,
    data?.spotlightedSpaceTabs,
    hashMap?.space,
    isResponsive,
    projectId,
    translate,
  ]);

  const currentSpace: Space | undefined = useMemo(() => {
    if (!!search?.trim() && _currentSpace) {
      return {
        ..._currentSpace,
        spaceName: translate('search_results'),
      };
    }

    return _currentSpace;
  }, [_currentSpace, search, translate]);

  const allSpaceTabs = useMemo(() => {
    return data?.spaces?.flatMap((space) => space.spaceTabs) ?? [];
  }, [data?.spaces]);

  const onSpaceClick = useCallback(
    (spaceId: UrlType | undefined) => {
      if (!spaceId) {
        removeKey('space');
        setSearch('');

        return;
      }
      setHashMap('space', spaceId.toString());
    },
    [removeKey, setHashMap],
  );

  useEffect(() => {
    if (!hashMap.space && !isResponsive) {
      const firstSpace = data?.spaces?.at(0);

      if (firstSpace) {
        setHashMap('space', firstSpace.spaceId.toString());
      }
    }
  }, [data?.spaces, hashMap.space, isResponsive, setHashMap]);

  const currentSpaceTabs = useMemo(() => {
    if (search?.trim()) {
      return (
        allSpaceTabs?.filter((e) =>
          e?.spaceTabTitle
            ?.toLowerCase?.()
            ?.includes(search?.trim?.().toLowerCase?.()),
        ) ?? []
      );
    }

    const spts = currentSpace?.spaceTabs;

    return (
      spts?.filter((e) =>
        e?.spaceTabTitle
          ?.toLowerCase?.()
          ?.includes(search?.trim?.().toLowerCase?.()),
      ) ?? []
    );
  }, [allSpaceTabs, currentSpace?.spaceTabs, search]);

  const onSpaceTabClick = useCallback(
    (spaceTab: SpaceTab) => {
      router.push({
        pathname: RoutePaths.customerPortalSpaceTabById
          .replace(':spaceTabId', spaceTab.spaceTabId.toString())
          .replaceAll(':spaceId', spaceTab.space.spaceId?.toString())
          .replaceAll(':projectId', projectId.toString()),
        hash: `#ppi=${projectId}`,
      });
    },
    [projectId, router],
  );

  return (
    <CpSectionWrapper
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
      isDisabled={disabled}
    >
      {isResponsive && currentSpace ? (
        <StyledBackWrapperInCpSpaces>
          <BackIconWrapper
            data-cy="cp.spaces.back"
            onClick={() => onSpaceClick(undefined)}
          >
            <ArrowLeft20 />
          </BackIconWrapper>
          <CpWidgetHeader.Title data-cy="cp.spaces.title" autoAdjustColor>
            {currentSpace?.spaceName}
          </CpWidgetHeader.Title>
        </StyledBackWrapperInCpSpaces>
      ) : (
        <CpWidgetHeader>
          <CpWidgetHeader.Title data-cy="cp.spaces.title" autoAdjustColor>
            {data.title}
          </CpWidgetHeader.Title>
          <CpWidgetHeader.Description
            data-cy="cp.spaces.description"
            autoAdjustColor
          >
            {data.description}
          </CpWidgetHeader.Description>
        </CpWidgetHeader>
      )}
      {!isResponsive || currentSpace ? (
        <SearchInput
          data-cy="cp.spaces.search"
          prefix={<Search16 />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={translate('search')}
          suffix={
            search ? <Close20 onClick={() => setSearch('')} /> : undefined
          }
        />
      ) : null}
      {isResponsive ? (
        <CpSpaceForMobile
          currentSpaceTabs={currentSpaceTabs}
          currentSpace={currentSpace}
          data={data}
          mode={mode}
          onSpaceClick={onSpaceClick}
          search={search}
          setMode={setMode}
          onSpaceTabClick={onSpaceTabClick}
        />
      ) : (
        <CpSpacesForLaptop
          currentSpaceTabs={currentSpaceTabs}
          currentSpace={currentSpace}
          data={data}
          mode={mode}
          onSpaceClick={onSpaceClick}
          search={search}
          setMode={setMode}
          onSpaceTabClick={onSpaceTabClick}
        />
      )}
    </CpSectionWrapper>
  );
};
